import React, { useEffect, useState } from "react";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import {
  Cue,
  ReverseRoundGuessPhasePayload,
  ReverseRoundResultsPayload,
} from "../../shared/interfaces/GameLogicTypes";
import SequenceBar from "../SequenceBar/SequenceBar";
import "./ReverseRoundPanel.css";

export interface ReverseRoundPanelProps {
  stage: 2;
  cues?: Cue[];
  guessPhase?: ReverseRoundGuessPhasePayload;
  onGuess?: (guessedId: string | null) => void;
  onHighRoll?: (accepted: boolean) => void;
  hasGuessed?: boolean;
  highRollDecided?: boolean;
  isMobile?: boolean;
  /** When set, the guess buttons flip to reveal correct/wrong */
  revealResults?: ReverseRoundResultsPayload | null;
  playerId?: string;
  onRevealComplete?: () => void;
}

export default function ReverseRoundPanel({
  guessPhase,
  cues,
  onGuess,
  onHighRoll,
  hasGuessed,
  highRollDecided,
  isMobile,
  revealResults,
  playerId,
  onRevealComplete,
}: ReverseRoundPanelProps) {
  const { t } = useTranslation();

  return (
    <Stage2Panel
      guessPhase={guessPhase}
      cues={cues}
      onGuess={onGuess}
      onHighRoll={onHighRoll}
      hasGuessed={hasGuessed}
      highRollDecided={highRollDecided}
      isMobile={isMobile}
      revealResults={revealResults}
      playerId={playerId}
      onRevealComplete={onRevealComplete}
      t={t}
    />
  );
}

// ─── Stage 2: Identity Guessing ─────────────────────────────────────────────

function Stage2Panel({
  guessPhase,
  cues,
  onGuess,
  onHighRoll,
  hasGuessed,
  highRollDecided,
  isMobile,
  revealResults,
  playerId,
  onRevealComplete,
  t,
}: {
  guessPhase?: ReverseRoundGuessPhasePayload;
  cues?: Cue[];
  onGuess?: (guessedId: string | null) => void;
  onHighRoll?: (accepted: boolean) => void;
  hasGuessed?: boolean;
  highRollDecided?: boolean;
  isMobile?: boolean;
  revealResults?: ReverseRoundResultsPayload | null;
  playerId?: string;
  onRevealComplete?: () => void;
  t: any;
}) {
  const [selectedGuess, setSelectedGuess] = useState<string | null>(null);
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    if (!revealResults) {
      setFlipped(false);
      return;
    }
    const flipTimer = setTimeout(() => setFlipped(true), 300);
    const completeTimer = setTimeout(() => onRevealComplete?.(), 3000);
    return () => {
      clearTimeout(flipTimer);
      clearTimeout(completeTimer);
    };
  }, [revealResults]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!guessPhase) return null;

  const handleGuess = (id: string) => {
    if (revealResults) return; // don't allow clicks during reveal
    setSelectedGuess(id);
    onGuess?.(id);
  };

  // Use canonical cues — SequenceBar maps them via the sequence numbers internally.
  const canonicalCues: Cue[] = cues ?? guessPhase.shownCues ?? [];

  const options = guessPhase.options;
  const gridClass = `rr-guess-grid options-${options.length}`;

  // Find correct answer for reveal
  let correctOptionId: string | null = null;
  if (revealResults && playerId) {
    for (const seq of revealResults.sequences) {
      const myGuess = seq.guesses.find((g) => g.playerId === playerId);
      if (myGuess) {
        correctOptionId = seq.isRandom ? "__not_one_of_us__" : seq.submittedBy;
        break;
      }
    }
  }

  const revealing = !!revealResults;

  return (
    <div className="rr-panel">
      <Typography className="rr-header">
        {t("rr_whose_ranking")}
      </Typography>

      {/* Show the assigned sequence as a read-only SequenceBar */}
      <SequenceBar
        readOnly
        sequence={guessPhase.shownSequence}
        cue1={canonicalCues[0] ?? { content: "#1" }}
        cue2={canonicalCues[1] ?? { content: "#2" }}
        cue3={canonicalCues[2] ?? { content: "#3" }}
        cue4={canonicalCues[3] ?? { content: "#4" }}
        cue5={canonicalCues[4] ?? { content: "#5" }}
        isMobile={isMobile}
        reset={() => {}} canSkip={false} decreaseRemainingSkips={() => {}} remainingSkips={0}
      />

      {/* High roll prompt */}
      {guessPhase.highRollAvailable && !highRollDecided && !revealing && (
        <Stack spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {t("rr_high_stakes_prompt")}
          </Typography>
          <div className="rr-highroll-prompt">
            <button
              className="rr-highroll-btn accept"
              onClick={() => onHighRoll?.(true)}
            >
              {t("rr_roll_it")}
            </button>
            <button
              className="rr-highroll-btn decline"
              onClick={() => onHighRoll?.(false)}
            >
              {t("rr_nah")}
            </button>
          </div>
        </Stack>
      )}

      {/* Guess buttons — flip in place when revealing */}
      {(highRollDecided || !guessPhase.highRollAvailable) && (
        <div className={gridClass}>
          {options.map((option) => {
            const isSelected = selectedGuess === option.id;
            const isCorrectOption = option.id === correctOptionId;

            if (revealing) {
              return (
                <div key={option.id} className="rr-guess-flip">
                  <div className={`rr-guess-flip-inner${flipped ? " flipped" : ""}`}>
                    <div
                      className={`rr-guess-btn rr-guess-flip-front${isSelected ? " selected" : ""}`}
                    >
                      {option.name}
                    </div>
                    <div className={`rr-guess-flip-back ${isCorrectOption ? "correct" : "wrong"}`}>
                      {isCorrectOption ? (
                        <EmojiEventsIcon sx={{ fontSize: 40, color: "#fff355" }} />
                      ) : (
                        <span style={{ fontSize: "2rem" }}>&#128128;</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            }

            let className = "rr-guess-btn";
            if (isSelected) className += " selected";

            return (
              <div
                key={option.id}
                className={className}
                onClick={() => handleGuess(option.id)}
              >
                {option.name}
              </div>
            );
          })}
        </div>
      )}

      {hasGuessed && !revealing && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {t("waiting_for_others")}
        </Typography>
      )}
    </div>
  );
}
